// src/hooks/useSessionWebSocket.js
import { useEffect, useState, useCallback, useRef } from 'react';
import { useWebSocket } from './useWebSocket';

export function useSessionWebSocket(sessionId) {
  const { 
    isConnected, 
    sendMessage: wsSendMessage, 
    addMessageHandler,
    subscribeToPersonalChat 
  } = useWebSocket();
  
  const [sessionStatus, setSessionStatus] = useState(null);
  const [roomAssignment, setRoomAssignment] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [personalChatMessages, setPersonalChatMessages] = useState([]);
  const [mediaRestrictions, setMediaRestrictions] = useState(null);
  const lastMessageIdRef = useRef(null);
  const lastPersonalMessageIdRef = useRef(null);

  // Subscribe to session live status and personal chat
  useEffect(() => {
    if (!isConnected || !sessionId) return;

    // Session-specific subscriptions
    wsSendMessage({
      type: 'subscribe_session_live',
      sessionId
    });

    wsSendMessage({
      type: 'subscribe_room_assignments',
      sessionId
    });

    wsSendMessage({
      type: 'subscribe_session_chat',
      sessionId
    });

    // Subscribe to media restrictions
    wsSendMessage({
      type: 'subscribe_media_restrictions',
      sessionId
    });

    // Also subscribe to personal chat for in-session personal messages
    subscribeToPersonalChat();

    return () => {
      // Unsubscribe when component unmounts
      wsSendMessage({
        type: 'unsubscribe_session_live',
        sessionId
      });
      
      wsSendMessage({
        type: 'unsubscribe_room_assignments',
        sessionId
      });
      
      wsSendMessage({
        type: 'unsubscribe_session_chat',
        sessionId
      });

      // Unsubscribe from media restrictions
      wsSendMessage({
        type: 'unsubscribe_media_restrictions',
        sessionId
      });
    };
  }, [isConnected, sessionId, wsSendMessage, subscribeToPersonalChat]);

  // Handle incoming messages
  useEffect(() => {
    const handleSessionStatus = (message) => {
      if (message.type === 'session_status_change' && 
          message.sessionId == sessionId) { // FIXED: Use == for type coercion
        setSessionStatus(message.status);
      }
    };

    const handleRoomAssignment = (message) => {
      if (message.type === 'room_assignment_update' && 
          message.sessionId == sessionId) { // FIXED: Use == for type coercion
        setRoomAssignment(message.roomData);
      }
    };

    const handleSessionChatMessage = (message) => {
      if (message.type === 'new_chat_message' && 
          message.sessionId == sessionId && // FIXED: Use == for type coercion
          message.chatType === 'session') {
        // Session-wide chat messages
        if (message.message && message.message.id !== lastMessageIdRef.current) {
          setChatMessages(prev => [...prev, message.message]);
          lastMessageIdRef.current = message.message.id;
        }
      }
    };

    // Handle personal/direct messages
    const handlePersonalMessage = (message) => {
      // Handle various personal message types
      if (message.type === 'direct_message' || 
          message.type === 'chat_message' ||
          (message.type === 'new_message' && message.chatType === 'normal')) {
        
        const msgData = message.message || message;
        if (msgData && msgData.id !== lastPersonalMessageIdRef.current) {
          setPersonalChatMessages(prev => [...prev, msgData]);
          lastPersonalMessageIdRef.current = msgData.id;
        }
      }
    };

    // Handle media restrictions update - FIXED: Use == for type coercion
    const handleMediaRestrictionsUpdate = (message) => {
      if (message.type === 'media_restrictions_update' && 
          message.sessionId == sessionId) { // FIXED: Changed === to ==
        console.log('🔢 Received media restrictions update:', message.restrictions);
        setMediaRestrictions(message.restrictions);
      }
    };

    const unsubStatus = addMessageHandler('session_status_change', handleSessionStatus);
    const unsubRoom = addMessageHandler('room_assignment_update', handleRoomAssignment);
    const unsubChat = addMessageHandler('new_chat_message', handleSessionChatMessage);
    const unsubDirect = addMessageHandler('direct_message', handlePersonalMessage);
    const unsubChatMsg = addMessageHandler('chat_message', handlePersonalMessage);
    const unsubNewMsg = addMessageHandler('new_message', handlePersonalMessage);
    const unsubMediaRestrictions = addMessageHandler('media_restrictions_update', handleMediaRestrictionsUpdate);

    return () => {
      unsubStatus();
      unsubRoom();
      unsubChat();
      unsubDirect();
      unsubChatMsg();
      unsubNewMsg();
      unsubMediaRestrictions();
    };
  }, [sessionId, addMessageHandler]);

  // Send message wrapper that includes sessionId
  const sendMessage = useCallback((type, data) => {
    if (!isConnected) return;
    
    wsSendMessage({
      type,
      sessionId: data.sessionId || sessionId,
      ...data
    });
  }, [isConnected, sessionId, wsSendMessage]);

  // Send personal chat message
  const sendPersonalMessage = useCallback((receiverId, messageData) => {
    if (!isConnected) return;
    
    wsSendMessage({
      type: 'send_chat_message',
      receiverId,
      sessionId, // Include session context
      ...messageData
    });
  }, [isConnected, sessionId, wsSendMessage]);

  return {
    isConnected,
    sessionStatus,
    roomAssignment,
    chatMessages,
    personalChatMessages,
    mediaRestrictions,
    setChatMessages,
    setPersonalChatMessages,
    sendMessage,
    sendPersonalMessage
  };
}